import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {AverageEnergyStatus} from '../../../../api/models/average-energy-status';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {EnergyType} from '../energy-chart/base-chart.component';
import {getAverageEnergyStatus} from '../energy-chart/average-energy-response.helper';

type DebugStatus = AverageEnergyStatus;

export type DebugValueMode = 'preserve' | 'positive' | 'negative';
export type DebugBatteryMode = 'power' | 'charge';

interface StatusEntry {
  mode: DebugValueMode;
  prefix: string;
  status?: DebugStatus;
}

interface DebugSectionConfig {
  key: string;
  label: string;
  type: EnergyType;
  batteryMode?: DebugBatteryMode;
}

export interface DebugTableRow {
  timestamp: string;
  value: number | null | undefined;
}

export interface DebugNodeTable {
  name: string;
  rows: DebugTableRow[];
}

export interface DebugTableSection {
  key: string;
  label: string;
  tables: DebugNodeTable[];
}

const DEBUG_SECTION_CONFIGS: DebugSectionConfig[] = [
  {key: 'production', label: 'Produktion', type: 'production'},
  {key: 'consumption', label: 'Verbrauch', type: 'consumption'},
  {key: 'battery-power', label: 'Speicher Leistung', type: 'battery', batteryMode: 'power'},
  {key: 'battery-charge', label: 'Speicher Ladezustand (SOC)', type: 'battery', batteryMode: 'charge'},
  {key: 'grid', label: 'Netz', type: 'grid'},
];

export function buildDebugStatusTables(
  data: AverageEnergyResponse | undefined,
  type: EnergyType,
  batteryMode: DebugBatteryMode = 'power',
): DebugNodeTable[] {
  return getDebugStatusEntries(data, type, batteryMode).flatMap((entry) => {
    const deviceTimeValues = (entry.status?.deviceTimeValues ?? {}) as Record<string, DateTimeValue[]>;
    const deviceNames = (entry.status?.deviceNames ?? {}) as Record<string, string>;
    const nodeIds = Array.from(new Set([...Object.keys(deviceNames), ...Object.keys(deviceTimeValues)]));

    return nodeIds.map((deviceKey) => {
      const rawTimeValues = deviceTimeValues[deviceKey];
      const timeValues = Array.isArray(rawTimeValues) ? rawTimeValues : [];
      const deviceName = deviceNames[deviceKey] || deviceKey;
      const tableLabel = entry.prefix ? `${entry.prefix}: ${deviceName}` : deviceName;

      return {
        name: tableLabel,
        rows: createTableRows(timeValues, entry.mode),
      };
    });
  });
}

export function buildAllDebugStatusSections(data: AverageEnergyResponse | undefined): DebugTableSection[] {
  return DEBUG_SECTION_CONFIGS
    .map((section) => ({
      key: section.key,
      label: section.label,
      tables: buildDebugStatusTables(data, section.type, section.batteryMode),
    }))
    .filter((section) => section.tables.length > 0);
}

export function getDebugStatusEntries(
  data: AverageEnergyResponse | undefined,
  type: EnergyType,
  batteryMode: DebugBatteryMode = 'power',
): StatusEntry[] {
  if (!data) {
    return [];
  }

  switch (type) {
    case 'production':
      return filterDefinedEntries([{status: getAverageEnergyStatus(data, 'production'), prefix: '', mode: 'preserve'}]);
    case 'consumption':
      if (getAverageEnergyStatus(data, 'consumption')) {
        return [{status: getAverageEnergyStatus(data, 'consumption'), prefix: 'Verbrauch', mode: 'preserve'}];
      }
      return filterDefinedEntries([
        {status: getAverageEnergyStatus(data, 'consumptionPositive'), prefix: 'Verbrauch +', mode: 'positive'},
        {status: getAverageEnergyStatus(data, 'consumptionNegative'), prefix: 'Verbrauch -', mode: 'negative'},
      ]);
    case 'battery':
      if (batteryMode === 'charge') {
        return filterDefinedEntries([{status: getAverageEnergyStatus(data, 'batteryCharge'), prefix: 'SOC', mode: 'preserve'}]);
      }
      if (getAverageEnergyStatus(data, 'batteryPower')) {
        return [{status: getAverageEnergyStatus(data, 'batteryPower'), prefix: 'Leistung', mode: 'preserve'}];
      }
      return filterDefinedEntries([
        {status: getAverageEnergyStatus(data, 'batteryPowerPositive'), prefix: 'Laden', mode: 'positive'},
        {status: getAverageEnergyStatus(data, 'batteryPowerNegative'), prefix: 'Entladen', mode: 'negative'},
      ]);
    case 'grid':
      if (getAverageEnergyStatus(data, 'grid')) {
        return [{status: getAverageEnergyStatus(data, 'grid'), prefix: 'Netz', mode: 'preserve'}];
      }
      return filterDefinedEntries([
        {status: getAverageEnergyStatus(data, 'gridPositive'), prefix: 'Bezug', mode: 'positive'},
        {status: getAverageEnergyStatus(data, 'gridNegative'), prefix: 'Einspeisung', mode: 'negative'},
      ]);
    default:
      return [];
  }
}

export function normalizeDebugStatusValue(value: number | undefined | null, mode: DebugValueMode): number | undefined | null {
  if (value === null || value === undefined) {
    return value;
  }

  switch (mode) {
    case 'positive':
      return Math.abs(value);
    case 'negative':
      return -Math.abs(value);
    case 'preserve':
    default:
      return value;
  }
}

export function formatDebugStatusValue(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '';
  }
  return value.toFixed(3);
}

function createTableRows(timeValues: DateTimeValue[], mode: DebugValueMode): DebugTableRow[] {
  return timeValues
    .map((item) => ({
      timestamp: item.read ?? '',
      value: normalizeDebugStatusValue(item.value, mode),
    }))
    .sort((a, b) => toTime(a.timestamp) - toTime(b.timestamp));
}

function filterDefinedEntries(entries: StatusEntry[]): StatusEntry[] {
  return entries.filter((entry) => !!entry.status);
}

function toTime(timestamp: string | undefined): number {
  if (!timestamp) {
    return Number.MAX_SAFE_INTEGER;
  }

  const time = new Date(timestamp).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}
